/************************************** THIRD-PARTY MODULES ***************************************/
import React from 'react';
import {observer, inject} from 'mobx-react';
import {Link, withRouter} from 'react-router-dom';
import {isBoolean, isAbsoluteURL, RouterProps} from 'mad-utils/lib/react';

/**************************************** PROJECT MODULES *****************************************/
import {navIfNoUnsavedData, onSubmitKeyPress, StoreProps} from '@canimmunize/core-imports';

/**************************************** TYPE DEFINITIONS ****************************************/
interface CILinkInnerImplementationProps extends RouterProps, StoreProps {
    /**
     * URL the link navigates to
     */
    to?: string;

    /**
     * UI content of link
     */
    children: ReactChildren;

    id?: string | null;
    className?: string;
    'aria-label'?: string;
    style?: React.CSSProperties;
    title?: string;

    /**
     * If true, show a confirmation prompt if unsaved data present onscreen
     */
    promptIfUnsaved?: boolean;

    /**
     * If true, open link in new tab
     */
    target?: '_blank' | null;

    /**
     * Function to run before following the link (unless runPreNavOnly: then
     * only run this function, don't follow the link)
     */
    preNavAction?: (() => any) | EventHandler;

    /**
     * If true, don't follow the link - only run the preNavAction
     */
    runPreNavOnly?: boolean;
}

/******************************************* COMPONENT ********************************************/
/**
 * Create Link that optionally accepts & injects [id] and/or [className] props
 * Display [children] string or React elements/components inside
 *
 * On click, run [preNavAction] (if given) then navigate to URL at [to] prop if
 * [runPreNavOnly] is not set to true - only run preNavAction in this case
 * ([to] must always be set, however, to display the URL in the status bar)
 *
 * To prevent confirm dialog on click, either set [target] to '_blank' (which
 * will open the link in a new tab) or [promptIfUnsaved] to false
 *
 * @param {Object} props Requires:
 *                           children {string|ReactElement}
 *                           At least one of:
 *                               to {string}
 *                               preNavAction {() => any}
 *                       Optional:
 *                           id {string}
 *                           className {string}
 *                           promptIfUnsaved {boolean}
 *                           runPreNavOnly {boolean}
 *
 * @return {JSX.Element} New link element (<a> or <Link>)
 */
@inject('appState')
@withRouter
@observer
export class CILink extends React.Component<CILinkInnerImplementationProps> {
    /**
     * Determine whether to show the unsaved prompt based on input values
     */
    private get shouldUseUnsavedPrompt(): boolean {
        if (this.props.target === `_blank`) {
            return false;
        }

        return isBoolean(this.props.promptIfUnsaved)
            ? this.props.promptIfUnsaved
            : !isAbsoluteURL(this.props.to);
    }

    /**
     * For target-specifying links, build & return <a> tag instead of <Link>
     * Triggered if target="_blank"
     *
     * These links open a new tab, and thus mustn't use the <Link> tag, which is
     * designed for in-app validation rather than linking per se
     */
    private get buildAnchorTagOutput() {
        const {to, id, className, target, children} = this.props;
        const cleantarget =
            typeof target !== `undefined` ? target : isAbsoluteURL(to) ? `_blank` : null;

        return this.shouldUseUnsavedPrompt ? (
            <a
                onClick={event => {
                    event.preventDefault();
                    event.stopPropagation();
                    return this.onClick(event);
                }}
                href={to}
                id={id}
                className={className}
                target={cleantarget}
                aria-label={this.props[`aria-label`] || null}
                title={this.props.title}
            >
                {children}
            </a>
        ) : (
            <a
                href={to}
                id={id}
                className={className}
                target={cleantarget}
                aria-label={this.props[`aria-label`] || null}
                title={this.props.title}
            >
                {children}
            </a>
        );
    }

    private buildDivTagOutput = () => {
        const {id, className, children, preNavAction} = this.props;
        const clickAction = this.shouldUseUnsavedPrompt ? this.onClick : preNavAction;

        return (
            <div
                role="link"
                id={id}
                className={className}
                onClick={clickAction}
                onKeyUp={onSubmitKeyPress(clickAction)}
                tabIndex={0}
                aria-label={this.props[`aria-label`] || undefined}
                title={this.props.title}
            >
                {children}
            </div>
        );
    };

    /**
     * If target and promptIfSaved props aren't defined, link shows a confirm
     * prompt before navigation if UI has unsaved data
     */
    private onClick = (event: React.SyntheticEvent) => {
        event.preventDefault();
        event.stopPropagation();

        const {to} = this.props;

        if (this.props.runPreNavOnly) {
            navIfNoUnsavedData(this.props.appState, this.props.preNavAction);
        } else {
            navIfNoUnsavedData(
                this.props.appState,
                this.props.history,
                to,
                this.props.preNavAction
            );
        }

        return false;
    };

    render() {
        const {children, to, className, style, title, id, target, preNavAction} = this.props;

        if (!to && !preNavAction) {
            throw new Error(`CILink must be given either a 'to' or a 'preNavAction' prop`);
        } else if (this.props.runPreNavOnly && !preNavAction) {
            throw new Error(
                `CILink: If runPreNavOnly prop is true, preNavAction prop must be provided`
            );
        }

        // Handle absolute or target-specifying links: return <a>, not <Link>
        // These links open an external tab
        if (target || isAbsoluteURL(to)) return this.buildAnchorTagOutput;

        // Handle links with only a run action, which should be set up to
        // avoid displaying the URL in the status bar
        if (!to) return this.buildDivTagOutput();

        // Handle standard links (contains to and/or preNavAction)
        return this.shouldUseUnsavedPrompt
            ? React.cloneElement(<Link to={to}>{children}</Link>, {
                  id,
                  className,
                  'aria-label': this.props[`aria-label`] || undefined,
                  style,
                  title,
                  onClick: this.onClick,
              })
            : React.cloneElement(<Link to={to}>{children}</Link>, {
                  id,
                  className,
                  'aria-label': this.props[`aria-label`] || undefined,
                  style,
                  title,
              });
    }
}
